import { FunctionComponent } from "react";

import { isCompanySourced, OnboardingListConfig } from "./onboardingListTypes";
import { ConfigFieldErrors } from "./onboardingListValidation";
import { AiCompanyForm, AiNewsForm } from "./configs/AiSearchForm";
import { FundingForm } from "./configs/FundingForm";
import { GmapsForm } from "./configs/GmapsForm";
import { HiringForm } from "./configs/HiringForm";
import { LeadDbForm } from "./configs/LeadDbForm";
import { LookalikeForm } from "./configs/LookalikeForm";
import { NewInRoleForm } from "./configs/NewInRoleForm";
import { PeopleAtCompanyFields } from "./configs/PeopleAtCompanyFields";
import { TopicForm } from "./configs/TopicForm";
import { OnboardingFilterOptions } from "./configs/useOnboardingFilterOptions";

interface ConfigFormProps {
	value: OnboardingListConfig;
	errors: ConfigFieldErrors;
	options: OnboardingFilterOptions;
	onChange: (value: OnboardingListConfig) => void;
}

/** The source filters of the selected type — what the list actually searches for. */
const SourceForm: FunctionComponent<ConfigFormProps> = ({ value, errors, options, onChange }) => {
	const shared = { errors, options, onChange };

	switch (value.kind) {
		case "hiring":
			return <HiringForm {...shared} value={value} />;
		case "lookalike":
			return <LookalikeForm {...shared} value={value} />;
		case "leadDb":
			return <LeadDbForm {...shared} value={value} />;
		case "newInRole":
			return <NewInRoleForm {...shared} value={value} />;
		case "topic":
			return <TopicForm {...shared} value={value} />;
		case "gmaps":
			return <GmapsForm {...shared} value={value} />;
		case "aiCompany":
			return <AiCompanyForm {...shared} value={value} />;
		case "aiNews":
			return <AiNewsForm {...shared} value={value} />;
		case "funding":
		case "mna":
			return <FundingForm {...shared} value={value} />;
	}
};

/**
 * Renders the inline config form matching the selected list type, followed by
 * the search profile for the types whose source only finds companies — without
 * it the created list has nobody to enrich and has to be finished in the in-app
 * details step.
 */
export const OnboardingListConfigForm: FunctionComponent<ConfigFormProps> = ({
	value,
	errors,
	options,
	onChange,
}) => (
	<>
		<SourceForm errors={errors} options={options} value={value} onChange={onChange} />
		{isCompanySourced(value) && (
			<PeopleAtCompanyFields
				errors={errors}
				options={options}
				typeKey={value.kind}
				value={value.peopleAtCompany}
				onChange={(peopleAtCompany) => onChange({ ...value, peopleAtCompany })}
			/>
		)}
	</>
);
